import * as React from "react";

type StickyItems = {
  /**
   * ref of the actual sticky element
   */
  ref: React.RefObject<HTMLElement>;
  /**
   * ref of the wrapper element, useful to get the real height and top properties without attaching a resize handler.
   */
  parentRef: React.RefObject<HTMLElement>;
  offsetHeight: number;
  offsetTop: number;
  /**
   * class to be applied when the element is sticky
   */
  className?: string;
  isSticky?: boolean;
}[];

type StickyStyle = React.CSSProperties;

interface IContext {
  register: (
    position: number,
    ref: React.RefObject<HTMLElement>,
    parentRef: React.RefObject<HTMLElement>,
    className?: string
  ) => void;
  getStyles: (position: number) => { style?: StickyStyle; className?: string };
}

const Context = React.createContext<IContext>({
  // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
  register: () => {},
  getStyles: () => {
    return {};
  },
});
const useStackedSticky = () => React.useContext(Context);

/**
 * Responsible for listening to the scroll event and updating the sticky elements
 * styling depending on their positions and the scroll state.
 */
export const StackedStickyProvider: React.FC = ({ children }) => {
  // Don't use state, we don't want to cause re-renders when registering an item.
  const items = React.useRef<StickyItems>([]);
  const [styles, setStyles] = React.useState<StickyStyle[]>([]);

  React.useEffect(() => {
    const scrollHandler = () => {
      let dirty = false;
      const updatedStyles: StickyStyle[] = [];

      // loop over all the registered elements and compute if they need to be sticky or not
      items.current.reduce((totalHeight, item, index) => {
        const initialState = item.isSticky;

        if (!item.parentRef.current) {
          return totalHeight;
        }

        // formula is scrollY + totalHeigth (position where things should start to be sticky)
        // >=
        // scrollY + container's top (don't use parentRef.current.offsetTop, doesn't work with relatively positioned elements)
        if (totalHeight >= item.parentRef.current.getBoundingClientRect().top) {
          updatedStyles[index] = {
            position: "fixed",
            top: totalHeight,
            width: item.parentRef.current.offsetWidth,
            zIndex: 400,
          };
          item.isSticky = true;
        } else {
          updatedStyles[index] = {
            position: "static",
          };
          item.isSticky = false;
        }

        // Verify if the position change, to avoid trigerring a useless render
        if (initialState !== item.isSticky) {
          dirty = true;
        }

        return totalHeight + item.parentRef.current.offsetHeight;
      }, 0);

      // apply a state update only if something changed.
      if (dirty) {
        setStyles(updatedStyles);
      }
    };

    window.addEventListener("scroll", scrollHandler);

    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  const register = (
    position: number,
    ref: React.RefObject<HTMLElement>,
    parentRef: React.RefObject<HTMLElement>,
    className?: string
  ) => {
    const { offsetHeight, offsetTop } = ref.current || {};
    if (offsetHeight === undefined || offsetTop === undefined) {
      return;
    }

    items.current[position] = { ref, parentRef, offsetHeight, offsetTop, className, isSticky: false };
  };

  // Safe getter
  const getStyles = (position: number): { style?: StickyStyle; className?: string } => {
    if (position - 1 <= styles.length) {
      return {
        style: styles[position],
        className: styles[position]?.position === "fixed" ? items.current[position].className : undefined,
      };
    }

    return {};
  };

  return <Context.Provider value={{ register, getStyles }}>{children}</Context.Provider>;
};

export const StackedSticky: React.FC<{ position: number; stickyClassName?: string }> = ({
  position,
  stickyClassName,
  children,
}) => {
  const [height, setHeight] = React.useState<number | undefined>(undefined);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const { register, getStyles } = useStackedSticky();

  React.useEffect(() => {
    setHeight(ref.current?.offsetHeight);
    register(position, ref, parentRef, stickyClassName);
  }, []);

  const { style, className } = getStyles(position);

  return (
    <div ref={parentRef} style={{ height, width: "100%" }}>
      <div ref={ref} style={style} className={className}>
        {children}
      </div>
    </div>
  );
};

/**
 * Usage
 *
 * somewhere up near the root of the tree, make sure to have the <StackedStickyProvider />
 *
 * in your component(s):
 *
 * const MyComponent: React.FC = () => (
 *  <StackedSticky position={0} stickyClassName="jambon">
 *   <div>Very Important Pickle</div>
 *  </StackedSticky>
 * )
 */
